import { delay, http, HttpResponse, passthrough } from 'msw';
import config from 'walden-fast/config/environment';
import type {
  PowerBiReportInternalCode,
  PowerBiReportPagesResponse,
} from 'walden-fast/types/fast-backend/power-bi';
import {
  chatMessageFactory,
  chatResponseFactory,
  invoiceFactory,
  notificationFactory,
  offsetAggregatedFactory,
  offsetDetailsFrom,
  offsetPurchaseFactory,
  sortAndPaginate,
  statFactory,
  ticketFactory,
  ticketSubmissionHandler,
  traceOrderFactory,
} from 'walden-fast/utils/mocks';
import type {
  FastBackendGetResponses,
  FastBackendPostResponses,
  FastMockedHandlers,
} from '../types/fast-backend/api-interface';
import type { MswArgs } from '../types/mocks';
import {
  accountFactory,
  emissionDetailDataFrom,
  emissionsAggregatedFactory,
  MOCK_KPIS,
  MOCK_POWER_BI_EMBEDDED_TOKEN,
  MOCK_POWER_BI_REPORT_PAGES_KPI,
  MOCK_POWER_BI_REPORT_PAGES_ORCHESTRATION,
  MOCK_POWER_BI_REPORT_PAGES_ORDER_TRACKING,
  MOCK_POWER_BI_REPORT_PAGES_OTC,
  MOCK_POWER_BI_REPORT_PAGES_TRANSPORT,
  MOCK_POWER_BI_REPORT_PAGES_WALDEN_GREEN,
  MOCK_POWER_BI_REPORT_PAGES_WALDEN_ML,
  MOCK_POWER_BI_REPORT_PAGES_WAREHOUSING,
  mockConnectInterfaces,
  userResponseFactory,
} from './mocks/fast-backend';
import { factoryList } from './mocks/lists';
import { organisationUserFactory } from './mocks/organisation-user';
import type { ContactSubmissionRequest } from 'walden-fast/types/fast-backend/contact';

const CHAT_MESSAGES = factoryList(1, chatMessageFactory);

const NOTIFICATIONS = factoryList(5, notificationFactory, [
  { read: false },
  {
    read: false,
    type: 'walden-view',
    description: 'Message details information and explanation',
  },
  { read: true },
  { read: true },
  { read: true },
]);

const TICKETS = factoryList(30 + Math.floor(Math.random() * 10), ticketFactory);
const TRACE_ORDERS = factoryList(10, traceOrderFactory);

const INVOICES = factoryList(29, invoiceFactory);

const TRACE_STATS = factoryList(2, statFactory, [
  {
    label: 'Important',
    unit: '%',
  },
  {
    label: 'Less Important',
  },
]);

const ORGANISATION_STATS = factoryList(3, statFactory, [
  {
    label: 'Walden BE',
    unit: 'Users',
  },
  {
    label: 'Walden NL',
    unit: 'Users',
  },
  {
    label: 'Walden FR',
    unit: 'Users',
  },
]);

const OFFSET_PURCHASES = factoryList(29, offsetPurchaseFactory);

const POWERBI_PAGES_PER_REPORT: Record<
  PowerBiReportInternalCode,
  PowerBiReportPagesResponse
> = {
  Warehousing: MOCK_POWER_BI_REPORT_PAGES_WAREHOUSING,
  Transport: MOCK_POWER_BI_REPORT_PAGES_TRANSPORT,
  Order2Cash: MOCK_POWER_BI_REPORT_PAGES_OTC,
  OrderTracking: MOCK_POWER_BI_REPORT_PAGES_ORDER_TRACKING,
  WaldenML: MOCK_POWER_BI_REPORT_PAGES_WALDEN_ML,
  Orchestration: MOCK_POWER_BI_REPORT_PAGES_ORCHESTRATION,
  KPI1: MOCK_POWER_BI_REPORT_PAGES_KPI,
  KPI2: MOCK_POWER_BI_REPORT_PAGES_KPI,
  KPI3: MOCK_POWER_BI_REPORT_PAGES_KPI,
  KPI4: MOCK_POWER_BI_REPORT_PAGES_KPI,
  KPI5: MOCK_POWER_BI_REPORT_PAGES_KPI,
  KPI6: MOCK_POWER_BI_REPORT_PAGES_KPI,
  WaldenGreen: MOCK_POWER_BI_REPORT_PAGES_WALDEN_GREEN,
};

const ACCOUNTS = factoryList(2, accountFactory, [
  {
    id: '1555',
    uniqueName: 'MonstersINC',
    displayName: 'Monsters Inc.',
    externalIdentifiers: {
      powerBI: {
        masterClientCode: 'mon',
      },
    },
  },
  {
    id: '5111',
    uniqueName: 'Brightwall',
    displayName: 'Brightwall',
    externalIdentifiers: {
      powerBI: {
        masterClientCode: 'bri',
      },
    },
  },
]);

const PASSTHROUGH_API_GET_URLS: string[] =
  config.msw.endpointsToMock === 'all' || config.msw.endpointsToMock === 'fast'
    ? []
    : [
        '/accounts',
        '/kpi',
        '/me',
        '/power-bi/embedded-token',
        '/power-bi/report/:internalName/pages',
        '/walden-green/emissions/aggregated',
        '/walden-green/emissions/detailed',
        '/connect/logistics-interfaces',
        '/walden-green/reports/yearly-emissions-report',
      ];

const GET_HANDLERS: FastMockedHandlers<FastBackendGetResponses> = {
  '/accounts': ACCOUNTS,
  '/me': userResponseFactory(),
  '/power-bi/embedded-token': MOCK_POWER_BI_EMBEDDED_TOKEN,
  '/power-bi/report/:internalName/pages': (args: MswArgs) => {
    const internalName = args.params?.[
      'internalName'
    ] as PowerBiReportInternalCode;
    return POWERBI_PAGES_PER_REPORT[internalName];
  },
  '/kpi': MOCK_KPIS,
  '/chat/messages': CHAT_MESSAGES,
  '/notifications': NOTIFICATIONS,
  '/contact/tickets': (args: MswArgs) => {
    return sortAndPaginate([...TICKETS], args.request);
  },
  '/walden-trace/orders': TRACE_ORDERS,
  '/stats/walden-trace': TRACE_STATS,
  '/stats/organisation': ORGANISATION_STATS,
  '/manage/organisation/users': factoryList(10, organisationUserFactory),
  '/connect/logistics-interfaces': () => {
    return mockConnectInterfaces();
  },
  '/walden-green/emissions/aggregated': async (args: MswArgs) => {
    await delay(1500);
    return emissionsAggregatedFactory({
      totalNumberOfShipments: {
        monthToDate:
          Number(args.request.headers.get('Impersonate-Account')) || 100,
        yearToDate:
          Number(args.request.headers.get('Impersonate-Account')) || 1200,
      },
    });
  },
  '/walden-green/offset/aggregated': offsetAggregatedFactory(),
  '/walden-green/emissions/detailed': async (args: MswArgs) => {
    await delay(1500);

    const url = new URL(args.request.url);

    const from = url.searchParams.get('fromDateInIso8601');
    if (!from) {
      return HttpResponse.text('Missing from param', { status: 400 });
    }

    return emissionDetailDataFrom(new Date(from));
  },
  '/walden-green/offset/details': async (args: MswArgs) => {
    await delay(1500);
    const url = new URL(args.request.url);
    const from = url.searchParams.get('from');
    if (!from) {
      return HttpResponse.text('Missing from param', { status: 400 });
    }

    return offsetDetailsFrom(new Date(from));
  },
  '/walden-green/offset_purchases': async (args: MswArgs) => {
    await delay(1500);

    return sortAndPaginate([...OFFSET_PURCHASES], args.request);
  },
  '/walden-green/reports/yearly-emissions-report': async (args: MswArgs) => {
    await delay(1500);

    const url = new URL(args.request.url);
    const year = url.searchParams.get('year');
    const clientCompanyId = url.searchParams.get('clientCompanyId');

    return {
      expiresAt: new Date(Date.now() + 1000 * 60 * 60).toISOString(),
      fileName: `Movianto-carbon-emission-report-${year}-${clientCompanyId}.pdf`,
      url: 'https://pdfobject.com/pdf/sample.pdf',
    };
  },
  '/invoices': (args: MswArgs) => {
    return sortAndPaginate([...INVOICES], args.request);
  },
};

const PASSTHROUGH_API_POST_URLS: string[] =
  config.msw.endpointsToMock === 'all' || config.msw.endpointsToMock === 'fast'
    ? []
    : ['/support', '/walden-chat/chat'];

const POST_HANDLERS: FastMockedHandlers<FastBackendPostResponses> = {
  '/support': async (args: MswArgs) => {
    const body = (await args.request.json()) as ContactSubmissionRequest;
    await delay(1000);
    return ticketSubmissionHandler(body);
  },
  '/walden-green/offset_purchases': async () => {
    await delay(2000);
    return true;
  },
  '/walden-chat/chat': async () => {
    await delay(1000);
    return chatResponseFactory();
  },
};

export const getHandlers = Object.keys(GET_HANDLERS).map(
  // @ts-expect-error: no good way to force typescript to know these keys are correct
  (url: keyof FastMockedHandlers<FastBackendGetResponses>) => {
    return http.get(`${config.fastApiURL}${url}`, async (...args) => {
      if (PASSTHROUGH_API_GET_URLS.includes(url)) {
        return passthrough();
      }

      let response;
      if (typeof GET_HANDLERS[url] === 'function') {
        response = GET_HANDLERS[url](...args);
      } else {
        response = GET_HANDLERS[url];
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return HttpResponse.json<any>(await maybeAwait(response));
    });
  },
);

export const postHandlers = Object.keys(POST_HANDLERS).map(
  // @ts-expect-error: no good way to force typescript to know these keys are correct
  (url: keyof FastMockedHandlers<FastBackendPostResponses>) => {
    return http.post(`${config.fastApiURL}${url}`, async (...args) => {
      if (PASSTHROUGH_API_POST_URLS.includes(url)) {
        return passthrough();
      }

      let response;
      if (typeof POST_HANDLERS[url] === 'function') {
        response = POST_HANDLERS[url](...args);
      } else {
        response = POST_HANDLERS[url];
      }

      const awaitedResponse = await maybeAwait(response);

      // Check if the response is a validation error (has status 400)
      if (
        awaitedResponse &&
        typeof awaitedResponse === 'object' &&
        'status' in awaitedResponse &&
        awaitedResponse.status &&
        typeof awaitedResponse.status === 'number'
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return HttpResponse.json<any>(awaitedResponse, {
          status: awaitedResponse.status,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return HttpResponse.json<any>(awaitedResponse);
    });
  },
);

async function maybeAwait(value: unknown) {
  // @ts-expect-error no good way to force typescript to know these keys are correct
  if (value && typeof value.then === 'function') {
    // eslint-disable-next-line @typescript-eslint/await-thenable
    return await value;
  }
  return value;
}
